import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Shield, Loader2, Check, BarChart3, Link2, AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
} from "@tabipla/ui";
import { DashboardLayout } from "@/presentation/layouts/DashboardLayout";
import { useSettings, useUpdateSettings } from "@/application/hooks/useSettings";
import { useDashboard } from "@/application/hooks/useDashboard";
import { useChangePassword, useLinkLine } from "@tabipla/hooks/auth";
import { useAuth } from "@/application/hooks/useAuth";
import { linkSocial, changePassword as changePasswordApi } from "@/infrastructure/auth/auth-client";
import { LoadingState } from "@/presentation/components/LoadingState";
import { ErrorState } from "@/presentation/components/ErrorState";

export function SettingsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: settings, isLoading, error, refetch } = useSettings();
  const { data: stats } = useDashboard();
  const updateSettings = useUpdateSettings();
  const { user } = useAuth();

  const { isLinkingLine, lineError, handleLinkLine } = useLinkLine({
    linkSocial,
    searchParams,
    setSearchParams,
    onSuccess: () => toast.success(t("settings.lineLinkSuccess", "LINEアカウントを連携しました")),
    t,
  });

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const {
    changePassword,
    isLoading: isChangingPassword,
    error: passwordError,
    isSuccess: passwordSuccess,
    reset: resetPasswordState,
  } = useChangePassword({
    changePasswordApi,
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setPasswordDialogOpen(false);
        resetPasswordState();
      }, 1500);
    },
  });

  const handleToggleSetting = async (
    key: "emailNotifications" | "pushNotifications" | "lineNotifications"
  ) => {
    if (!settings) return;
    await updateSettings.mutateAsync({ [key]: !settings[key] });
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    await changePassword({ currentPassword, newPassword, confirmPassword });
  };

  const getPasswordErrorMessage = (errorCode: string | null) => {
    if (!errorCode) return null;
    switch (errorCode) {
      case "PASSWORD_MISMATCH":
        return t("settings.passwordMismatch");
      case "PASSWORD_TOO_SHORT":
        return t("settings.passwordTooShort");
      default:
        return t("settings.passwordChangeFailed");
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title={t("settings.title")}>
        <LoadingState message={t("settings.loading")} />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title={t("settings.title")}>
        <ErrorState error={error} onRetry={() => refetch()} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("settings.title")}>
      <div className="animate-fade-in space-y-6">
        {/* Header - Desktop only */}
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold tracking-tight">{t("settings.title")}</h1>
          <p className="text-muted-foreground">{t("settings.subtitle")}</p>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-lg">
                <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <Bell className="h-4 w-4 text-primary" />
                </div>
                {t("settings.notifications")}
              </CardTitle>
              <CardDescription>{t("settings.notificationsDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between rounded-xl border p-4 transition-colors hover:bg-accent/50">
                <div className="flex-1">
                  <p className="font-medium">{t("settings.emailNotifications")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.emailNotificationsDescription")}
                  </p>
                </div>
                <Switch
                  checked={settings?.emailNotifications ?? false}
                  onCheckedChange={() => handleToggleSetting("emailNotifications")}
                  disabled={updateSettings.isPending}
                />
              </div>
              <div className="flex items-center justify-between rounded-xl border p-4 transition-colors hover:bg-accent/50">
                <div className="flex-1">
                  <p className="font-medium">{t("settings.pushNotifications")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.pushNotificationsDescription")}
                  </p>
                </div>
                <Switch
                  checked={settings?.pushNotifications ?? false}
                  onCheckedChange={() => handleToggleSetting("pushNotifications")}
                  disabled={updateSettings.isPending}
                />
              </div>
              {user?.lineUserId && (
                <div className="flex items-center justify-between rounded-xl border border-[#06C755]/30 bg-[#06C755]/5 p-4 transition-colors hover:bg-[#06C755]/10">
                  <div className="flex-1">
                    <p className="font-medium">{t("settings.lineNotifications", "LINE通知")}</p>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.lineNotificationsDescription", "LINEで通知を受け取る")}
                    </p>
                  </div>
                  <Switch
                    checked={settings?.lineNotifications ?? false}
                    onCheckedChange={() => handleToggleSetting("lineNotifications")}
                    disabled={updateSettings.isPending}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-lg">
                <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <Shield className="h-4 w-4 text-primary" />
                </div>
                {t("settings.security")}
              </CardTitle>
              <CardDescription>{t("settings.securityDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between rounded-xl border p-4 transition-colors hover:bg-accent/50">
                <div className="flex-1">
                  <p className="font-medium">{t("settings.changePassword")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.changePasswordDescription")}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPasswordDialogOpen(true)}>
                  {t("settings.change")}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-lg">
                <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#06C755]/10">
                  <svg className="h-4 w-4 text-[#06C755]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                </div>
                {t("settings.lineIntegration", "LINE連携")}
              </CardTitle>
              <CardDescription>
                {t(
                  "settings.lineIntegrationDescription",
                  "LINEアカウントを連携してログインや通知を受け取れます"
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between rounded-xl border p-4 transition-colors hover:bg-accent/50">
                <div className="flex-1">
                  <p className="font-medium">
                    {user?.lineUserId
                      ? t("settings.lineConnected", "LINE連携済み")
                      : t("settings.lineNotConnected", "LINE未連携")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user?.lineUserId
                      ? t("settings.lineConnectedDescription", "LINEアカウントが連携されています")
                      : t(
                          "settings.lineNotConnectedDescription",
                          "LINEアカウントを連携すると、LINEでログインできます"
                        )}
                  </p>
                </div>
                {user?.lineUserId ? (
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <Check className="h-4 w-4" />
                    {t("settings.connected", "連携中")}
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLinkLine}
                    disabled={isLinkingLine}
                    className="border-[#06C755] text-[#06C755] hover:bg-[#06C755]/10"
                  >
                    {isLinkingLine ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="mr-2 h-4 w-4" />
                    )}
                    {t("settings.linkLine", "連携する")}
                  </Button>
                )}
              </div>
              {lineError && (
                <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {lineError}
                </div>
              )}
            </CardContent>
          </Card>

          {stats && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-lg">
                  <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </div>
                  {t("settings.usage")}
                </CardTitle>
                <CardDescription>{t("settings.usageDescription")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="rounded-lg border p-3">
                    <p className="text-2xl font-bold">{stats.totalTrips}</p>
                    <p className="text-xs text-muted-foreground">{t("settings.totalTrips")}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-2xl font-bold">{stats.groupCount}</p>
                    <p className="text-xs text-muted-foreground">{t("settings.groups")}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-2xl font-bold">{stats.completedTrips}</p>
                    <p className="text-xs text-muted-foreground">{t("settings.completedTrips")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("settings.changePassword")}</DialogTitle>
            <DialogDescription>{t("settings.changePasswordDialogDescription")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("settings.currentPassword")}</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("settings.newPassword")}</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("settings.confirmPassword")}</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            {passwordError && (
              <p className="text-sm text-destructive">{getPasswordErrorMessage(passwordError)}</p>
            )}
            {passwordSuccess && (
              <p className="flex items-center gap-2 text-sm text-green-600">
                <Check className="h-4 w-4" />
                {t("settings.passwordChangeSuccess")}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordDialogOpen(false)}
                disabled={isChangingPassword}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("common.saving")}
                  </>
                ) : (
                  t("settings.changePassword")
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
